import * as React from 'react';
import cn from 'classnames';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
	text?: string;
	id?: string;
	type?: 'button' | 'submit';
	size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
	outlineButton?: boolean;
	color?: 'primary' | 'accent';
};

export default function Button({
	text = '',
	id = undefined,
	type = 'button',
	size = 'base',
	outlineButton = false,
	color = 'accent',
	...rest
}: ButtonProps): React.ReactElement {
	const buttonClassNames = () => {
		const buttonClasses = cn(
			'rounded font-bold transition-colors cursor-pointer disabled:cursor-default',
			{ 'px-[9px] py-[5px] text-xs': size === 'xs' },
			{ 'px-[11px] py-[7px] text-sm': size === 'sm' },
			{ 'px-[15px] py-[7px] text-sm ': size === 'base' },
			{ 'px-[15px] py-[7px] text-base': size === 'lg' },
			{ 'px-[23px] py-[11px] text-base ': size === 'xl' },
			` ${
				outlineButton
					? 'bg-white border-2 hover:text-white disabled:border-link-disabled disabled:bg-white disabled:text-link-disabled'
					: 'text-white border-2 focus:outline focus:outline-2 focus:outline-offset-2 disabled:border-link-disabled disabled:bg-link-disabled'
			}`,
			{
				'text-accent-500 border-accent-500 hover:border-accent-700 hover:bg-accent-700':
					outlineButton && color === 'accent',
			},
			{
				'text-primary-700 border-primary-700 hover:border-primary-600 hover:bg-primary-600':
					outlineButton && color === 'primary',
			},
			{
				'bg-accent-500 border-accent-500 hover:border-accent-700 hover:bg-accent-700 focus:outline-accent-500 focus:hover:outline-accent-700':
					!outlineButton && color === 'accent',
			},
			{
				'bg-primary-700 border-primary-700 hover:border-primary-600 hover:bg-primary-600 focus:outline-primary-700 focus:hover:outline-primary-600':
					!outlineButton && color === 'primary',
			}
		);

		return buttonClasses;
	};

	// TS linter does not like dynamic button types
	// Docs/Forum recommend rendering based on type
	if (type === 'submit') {
		return (
			<button
				id={id}
				type="submit"
				className={buttonClassNames()}
				{...rest}
			>
				{text}
			</button>
		);
	}

	return (
		<button id={id} type="button" className={buttonClassNames()} {...rest}>
			{text}
		</button>
	);
}
